import { HttpParams } from '@angular/common/http';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';
import { Observable, takeUntil } from 'rxjs';

type HistoryFieldCarrier = {
  changedFieldsArray?: string[];
  changedFields?: string;
};

type HistoryResponse<T> = {
  data?: T[];
  count?: number;
};

export const HISTORY_PAGE_SIZE = 50;

export function buildHistoryParams(entityKey: string, entityId: string, page = 0): HttpParams {
  return new HttpParams()
    .set(entityKey, entityId.toString())
    .set('page', page.toString())
    .set('pageSize', HISTORY_PAGE_SIZE.toString())
    .set('orderBy', 'dateUpdated')
    .set('sort', 'desc');
}

export function loadPopupHistory<T>(
  request$: Observable<HistoryResponse<T>>,
  destroy$: Observable<unknown>,
  mapRecord: (history: T) => T,
  onSuccess: (records: T[], total: number) => void,
  onError: (error: unknown) => void,
): void {
  request$.pipe(takeUntil(destroy$)).subscribe({
    next: (response) => {
      const records = (response?.data ?? []).map(mapRecord);
      onSuccess(records, response?.count ?? records.length);
    },
    error: onError,
  });
}

/** 1-based first and last row numbers of a history page, for the "Showing x–y of z" label. */
export function historyPageRange(pageIndex: number, rowsOnPage: number): { start: number; end: number } {
  const start = rowsOnPage ? pageIndex * HISTORY_PAGE_SIZE + 1 : 0;
  return { start, end: rowsOnPage ? start + rowsOnPage - 1 : 0 };
}

export function isHistoryFieldChanged(history: HistoryFieldCarrier, ...fieldNames: string[]): boolean {
  const changedFields = getHistoryChangedFields(history);
  return fieldNames.some(field => changedFields.includes(field));
}

export function getHistoryDateTimeDisplay(
  value: string | undefined,
  dateFormatService: DateFormatService,
): string {
  if (!value) {
    return '-';
  }

  return dateFormatService.convertUTCDateStringToUserTimeZoneString(
    value,
    dateFormatService.userDateWithTimeFormat,
  );
}
